import * as Schema from "effect/Schema";

export const CommunicationId = Schema.String.check(
  Schema.isMinLength(1),
  Schema.isMaxLength(2048),
  Schema.isPattern(/^[^\r\n]*$/),
);
export const TransportId = Schema.String.check(Schema.isPattern(/^[a-z][a-z0-9_-]{0,63}$/));
export const CommunicationConnectionRef = Schema.Struct({
  transport: TransportId,
  environmentId: CommunicationId,
  projectId: CommunicationId,
  connectionId: CommunicationId,
});
export type CommunicationConnectionRef = typeof CommunicationConnectionRef.Type;
export const CommunicationCapabilities = Schema.Struct({
  receive: Schema.Boolean,
  receipts: Schema.Boolean,
  directory: Schema.Boolean,
  history: Schema.Boolean,
  attachments: Schema.Boolean,
  presentation: Schema.Boolean,
  threads: Schema.Boolean,
  actions: Schema.Boolean,
  maxAttachmentBytes: Schema.Int.check(Schema.isGreaterThanOrEqualTo(0)),
});
export const CommunicationConnectionDescriptor = Schema.Struct({
  connection: CommunicationConnectionRef,
  name: Schema.String,
  capabilities: CommunicationCapabilities,
});
export type CommunicationConnectionDescriptor = typeof CommunicationConnectionDescriptor.Type;
export const CommunicationChannelBinding = Schema.Struct({
  connectorId: Schema.Literal("communication"),
  bindingId: CommunicationId,
});
export const CommunicationLease = Schema.Struct({
  connection: CommunicationConnectionRef,
  generation: Schema.Int.check(Schema.isGreaterThan(0)),
});
export type CommunicationLease = typeof CommunicationLease.Type;
export const CommunicationActor = Schema.Struct({
  projectId: CommunicationId,
  principalId: CommunicationId,
  conversationId: Schema.optionalKey(CommunicationId),
  runId: Schema.optionalKey(CommunicationId),
  revision: Schema.optionalKey(CommunicationId),
  requestId: Schema.optionalKey(CommunicationId),
  grantId: Schema.optionalKey(CommunicationId),
});
export type CommunicationActor = typeof CommunicationActor.Type;
export const CommunicationTarget = Schema.Union([
  Schema.Struct({ kind: Schema.Literal("self") }),
  Schema.Struct({
    kind: Schema.Literal("contacts"),
    addresses: Schema.Array(CommunicationId).check(Schema.isMinLength(1), Schema.isMaxLength(20)),
  }),
  Schema.Struct({ kind: Schema.Literal("channel"), address: CommunicationId }),
]);
export type CommunicationTarget = typeof CommunicationTarget.Type;
export const CommunicationDestination = Schema.Union([
  Schema.Struct({ kind: Schema.Literal("origin") }),
  Schema.Struct({ kind: Schema.Literal("binding"), bindingId: CommunicationId }),
  Schema.Struct({
    kind: Schema.Literal("addressed"),
    transport: TransportId,
    connectionId: Schema.optionalKey(CommunicationId),
    target: CommunicationTarget,
    discussion: Schema.Union([
      Schema.Struct({ kind: Schema.Literal("new") }),
      Schema.Struct({ kind: Schema.Literal("reuse"), key: CommunicationId }),
    ]),
  }),
]);
export type CommunicationDestination = typeof CommunicationDestination.Type;
/** Loop report policy is separate from native transport addressing. */
export const CommunicationLoopDelivery = Schema.Struct({
  kind: Schema.Literal("communication"),
  destination: CommunicationDestination,
  thread: Schema.Literals(["ongoing", "per_run"]),
});
export type CommunicationLoopDelivery = typeof CommunicationLoopDelivery.Type;
export const CommunicationMessage = Schema.Struct({
  title: Schema.optionalKey(
    Schema.String.check(Schema.isMaxLength(998), Schema.isPattern(/^[^\r\n]*$/)),
  ),
  text: Schema.String.check(Schema.isMaxLength(200000)),
  attachments: Schema.Array(CommunicationId).check(Schema.isMaxLength(10)),
  actions: Schema.Array(
    Schema.Struct({ id: CommunicationId, label: Schema.String.check(Schema.isMaxLength(100)) }),
  ).check(Schema.isMaxLength(10)),
});
export type CommunicationMessage = typeof CommunicationMessage.Type;
export const DeliveryIntent = Schema.Struct({
  sourceKey: CommunicationId,
  actor: CommunicationActor,
  destination: CommunicationDestination,
  replyBindingId: Schema.optionalKey(CommunicationId),
  message: CommunicationMessage,
  requiresReply: Schema.optionalKey(Schema.Boolean),
  requiresMembers: Schema.optionalKey(Schema.Boolean),
});
export type DeliveryIntent = typeof DeliveryIntent.Type;
export const CommunicationDeliveryState = Schema.Literals([
  "pending",
  "sending",
  "accepted",
  "rejected",
  "uncertain",
  "cancelled",
]);
export const CommunicationReceipt = Schema.Struct({
  id: CommunicationId,
  bindingId: CommunicationId,
  state: CommunicationDeliveryState,
  acceptedParts: Schema.Int.check(Schema.isGreaterThanOrEqualTo(0)),
  observed: Schema.optionalKey(Schema.Literals(["delivered", "bounced", "unknown"])),
  error: Schema.optionalKey(Schema.String),
});
export type CommunicationReceipt = typeof CommunicationReceipt.Type;
export class CommunicationError extends Schema.TaggedErrorClass<CommunicationError>()(
  "CommunicationError",
  {
    reason: Schema.Literals([
      "unavailable",
      "unauthorized",
      "unknown_transport",
      "ambiguous_destination",
      "unsupported",
      "invalid_input",
      "conflict",
      "busy",
    ]),
    message: Schema.String,
  },
) {}
